import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Phone, Mail, MapPin, MessageSquare, Loader2 } from 'lucide-react';
import { contactInfo } from '../mock';
import { useToast } from '../hooks/use-toast';
import { getSiteSession } from '../lib/track';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    company_site: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, { ...formData, session_id: getSiteSession() });
      
      if (response.status === 200 || response.status === 201) {
        toast({
          title: "Mensagem enviada com sucesso!",
          description: "Entraremos em contato em breve.",
        });
        setFormData({ name: '', email: '', phone: '', message: '', company_site: '' });
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      
      const errorMessage = error.response?.data?.detail || 
                          'Erro ao enviar mensagem. Tente novamente.';
      
      toast({
        title: "Erro ao enviar mensagem",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contato" className="py-20 bg-gradient-to-br from-slate-800 to-slate-900 overflow-hidden" style={{ scrollMarginTop: '100px' }}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Fale com nossa Empresa de TI</h2>
          <p className="text-xl text-blue-300">Solicite um orçamento de serviços de TI para empresas ou suporte técnico empresarial.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot anti-spam — oculto para humanos, preenchido por bots */}
                <input
                  type="text"
                  name="company_site"
                  value={formData.company_site}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                />
                <div>
                  <Input
                    name="name"
                    placeholder="Seu nome"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-slate-900 border-slate-600 text-white placeholder:text-gray-400 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Seu e-mail"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-slate-900 border-slate-600 text-white placeholder:text-gray-400 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <Input
                    name="phone"
                    type="tel"
                    placeholder="Seu telefone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="bg-slate-900 border-slate-600 text-white placeholder:text-gray-400 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <Textarea
                    name="message"
                    placeholder="Sua mensagem"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="bg-slate-900 border-slate-600 text-white placeholder:text-gray-400 focus:border-cyan-500 resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold py-6 text-lg shadow-lg shadow-blue-500/30 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Enviar Mensagem'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700 hover:border-cyan-500 transition-all duration-300">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="bg-gradient-to-br from-blue-600 to-cyan-500 p-4 rounded-xl">
                  <Phone className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Telefone</h3>
                  <a href={`tel:${contactInfo.phone}`} className="text-blue-300 hover:text-cyan-400 transition-colors">
                    {contactInfo.phone}
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700 hover:border-cyan-500 transition-all duration-300">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="bg-gradient-to-br from-green-600 to-green-500 p-4 rounded-xl">
                  <MessageSquare className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">WhatsApp</h3>
                  <a
                    href={`https://wa.me/${contactInfo.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-300 hover:text-cyan-400 transition-colors"
                  >
                    {contactInfo.phone}
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700 hover:border-cyan-500 transition-all duration-300">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="bg-gradient-to-br from-blue-600 to-cyan-500 p-4 rounded-xl">
                  <Mail className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">E-mail</h3>
                  <a href={`mailto:${contactInfo.email}`} className="text-blue-300 hover:text-cyan-400 transition-colors">
                    {contactInfo.email}
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700 hover:border-cyan-500 transition-all duration-300">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="bg-gradient-to-br from-blue-600 to-cyan-500 p-4 rounded-xl">
                  <MapPin className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Endereço</h3>
                  <p className="text-blue-300">{contactInfo.fullAddress}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;